import {
  OneOfElements,
  ProductElementOverrides,
} from '@shared/models/project.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { ElementQuantityRecord } from '@shared/models/element-quantities.model';
import { mergeVersionActionsPartials } from '../../actions/building-version-actions.helpers';

/**
 * Merge two property arrays by property name.
 * When the same name exists in both, merge count (resolved, expression, formatted).
 *
 * @param base - Base properties (e.g. from first update)
 * @param next - Next properties (e.g. from second update)
 * @returns Merged array
 */
const mergePropertiesByName = (
  base: IElementProperty[],
  next: IElementProperty[],
): IElementProperty[] => {
  const byName = new Map<string, IElementProperty>();

  for (const p of base) {
    byName.set(p.name, { ...p });
  }

  for (const p of next) {
    const existing = byName.get(p.name);
    const existingCount = existing?.count;
    const nextCount = p.count;
    const bothCountsAreObjects =
      typeof existingCount === 'object' &&
      existingCount !== null &&
      typeof nextCount === 'object' &&
      nextCount !== null;
    if (existing && bothCountsAreObjects) {
      byName.set(p.name, {
        ...existing,
        count: { ...existingCount, ...nextCount },
      } as IElementProperty);
    } else if (existing) {
      byName.set(p.name, { ...existing, ...p } as IElementProperty);
    } else {
      byName.set(p.name, { ...p } as IElementProperty);
    }
  }

  return Array.from(byName.values());
};

/** Merge two quantity records so count/fallbackCount are merged (e.g. keep expression from a, resolved from b) */
const mergeQuantityRecords = (
  a: ElementQuantityRecord,
  b: ElementQuantityRecord,
): ElementQuantityRecord => {
  const merged = { ...a, ...b } as ElementQuantityRecord;
  for (const key of Object.keys(merged) as (keyof typeof merged)[]) {
    const aVal = a[key];
    const bVal = b[key];
    if (
      aVal &&
      bVal &&
      typeof aVal === 'object' &&
      typeof bVal === 'object' &&
      !Array.isArray(aVal) &&
      !Array.isArray(bVal)
    ) {
      const entry = { ...aVal, ...bVal } as Record<string, unknown>;
      if (
        'count' in aVal &&
        aVal.count &&
        'count' in bVal &&
        bVal.count &&
        typeof aVal.count === 'object' &&
        typeof bVal.count === 'object'
      ) {
        entry.count = { ...aVal.count, ...bVal.count };
      }
      if (
        'fallbackCount' in aVal &&
        aVal.fallbackCount &&
        'fallbackCount' in bVal &&
        bVal.fallbackCount &&
        typeof aVal.fallbackCount === 'object' &&
        typeof bVal.fallbackCount === 'object'
      ) {
        entry.fallbackCount = {
          ...aVal.fallbackCount,
          ...bVal.fallbackCount,
        };
      }
      (merged as Record<string, unknown>)[key] = entry;
    }
  }
  return merged;
};

/** Shallow-merge override maps so sequential changes can set different targets */
const mergeProductOverrides = (
  a: ProductElementOverrides,
  b: ProductElementOverrides,
): ProductElementOverrides => ({
  ...a,
  ...b,
});

/**
 * Merge two Partial<OneOfElements> into one.
 * Used when multiple updates target the same element; batch-update applies a single partial per element.
 *
 * @param a - First partial
 * @param b - Second partial (takes precedence for non-array keys)
 * @returns Merged partial
 */
export const mergeElementPartials = (
  a: Partial<OneOfElements>,
  b: Partial<OneOfElements>,
): Partial<OneOfElements> => {
  const merged = { ...a, ...b } as Record<string, unknown>;

  if ('properties' in a && a.properties && 'properties' in b && b.properties) {
    merged.properties = mergePropertiesByName(a.properties, b.properties);
  }
  if ('quantity' in a && a.quantity && 'quantity' in b && b.quantity) {
    merged.quantity = mergeQuantityRecords(a.quantity, b.quantity);
  }
  if ('count' in a && a.count && 'count' in b && b.count) {
    merged.count = { ...a.count, ...b.count };
  }
  if (
    'productOverrides' in a &&
    a.productOverrides &&
    'productOverrides' in b &&
    b.productOverrides
  ) {
    merged.productOverrides = mergeProductOverrides(
      a.productOverrides,
      b.productOverrides,
    );
  }
  if ('actions' in a && a.actions && 'actions' in b && b.actions) {
    merged.actions = mergeVersionActionsPartials(a.actions, b.actions);
  }

  return merged as Partial<OneOfElements>;
};
